<template>
  <div class="v1-keypad" role="group" :aria-label="ariaLabel">
    <button
      v-for="key in keys"
      :key="key.value"
      type="button"
      class="v1-keypad__key"
      :class="{
        'v1-keypad__key--action': key.action,
        'v1-keypad__key--spacer': key.spacer,
      }"
      :disabled="key.spacer"
      :tabindex="key.spacer ? -1 : 0"
      :aria-hidden="key.spacer || undefined"
      :aria-label="key.value === 'backspace' ? backspaceLabel : undefined"
      :data-testid="`v1-keypad-${key.value}`"
      @click="onPress(key)"
    >
      <span v-if="key.value === 'backspace'" class="v1-keypad__icon" aria-hidden="true">⌫</span>
      <template v-else>{{ key.label }}</template>
    </button>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";

interface KeypadKey {
  value: string;
  label: string;
  action?: boolean;
  spacer?: boolean;
}

/**
 * On-screen numeric keypad for the v1 other-amount screen. Replaces the device's
 * native keyboard entirely (the amount is rendered as text, never an input), so
 * the OS keyboard never appears in the webview. Emits `press` with a digit
 * ("0"–"9"), "decimal", or "backspace"; the parent owns the amount string.
 *
 * The decimal key is shown only when the currency has a minor unit and displays
 * the locale's decimal separator; otherwise its slot is an inert spacer.
 */
export default defineComponent({
  name: "V1NumericKeypad",
  props: {
    allowsDecimals: { type: Boolean, default: true },
    decimalSeparator: { type: String, default: "." },
    ariaLabel: { type: String, default: "Numeric keypad" },
    backspaceLabel: { type: String, default: "Delete" },
  },
  emits: ["press"],
  setup(props, { emit }) {
    const keys = computed<KeypadKey[]>(() => {
      const digits: KeypadKey[] = ["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => ({ value: d, label: d }));
      const decimalKey: KeypadKey = props.allowsDecimals
        ? { value: "decimal", label: props.decimalSeparator }
        : { value: "spacer", label: "", spacer: true };
      return [...digits, decimalKey, { value: "0", label: "0" }, { value: "backspace", label: "", action: true }];
    });

    const onPress = (key: KeypadKey): void => {
      if (key.spacer) return;
      emit("press", key.value);
    };

    return { keys, onPress };
  },
});
</script>

<style scoped>
.v1-keypad {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  width: 100%;
}

.v1-keypad__key {
  width: auto;
  min-height: 56px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  border-radius: var(--pj-radius-xl, 16px);
  background: transparent;
  font-family: "Inter", sans-serif;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  letter-spacing: -0.5px;
  color: var(--pj-color-brand-gray-850, #222926);
  cursor: pointer;
}

.v1-keypad__key:active {
  background: var(--pj-color-brand-gray-150, #f1f0ee);
}

.v1-keypad__key--spacer {
  visibility: hidden;
  cursor: default;
}

.v1-keypad__icon {
  font-size: 22px;
  line-height: 1;
}
</style>
